import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import EmptyState from '@/components/ui/EmptyState'
import Money from '@/components/ui/Money'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TermTip from '@/components/ui/TermTip'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useBreakouts, useIndexSymbols } from '@/features/signals/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { formatDate } from '@/lib/format'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { BreakoutDirection, LevelRow } from '@/schemas/signals'
import {
  breakoutsQueryOf,
  breakoutsSearchSchema,
  loadBreakouts,
} from '@/screens/signals/BreakoutsScreen.loader'
import SessionDatePicker from '@/screens/signals/components/SessionDatePicker'
import MissingFigure from '@/screens/signals/components/MissingFigure'
import { signalCompanyColumn } from '@/screens/signals/components/SignalCompanyColumn'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsLocked from '@/screens/signals/components/SignalsLocked'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import SignalsTableFrame from '@/screens/signals/components/SignalsTableFrame'
import { PickerSkeleton } from '@/screens/signals/components/SignalsSkeletons'
import {
  TableFrameSkeleton,
  TabsSkeleton,
  ToolbarSkeleton,
} from '@/screens/signals/components/SignalsTableSkeletons'
import SignalsToolbar from '@/screens/signals/components/SignalsToolbar'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { SIGNAL_ROWS_52 } from '@/screens/signals/lib/signal-rows'
import { onSession, useSessionStatus } from '@/screens/signals/lib/session-status'
import { useShownValue } from '@/screens/signals/lib/shown-value'
import { isSignalsLocked, signalRowLink } from '@/screens/signals/lib/signals-ui'

const TITLE = 'Breakout Finder'

/** The strip names the page, so the header is the bare title (`sr-only` from `lg`). */
const HEADER = <TitleRow title={TITLE} />

/** Ties the direction tabs to the one panel under them. */
const TABS_ID = 'breakouts'

const DIRECTIONS: readonly TabBarOption[] = [
  { value: 'above', label: 'Pushed Above' },
  { value: 'below', label: 'Fell Below' },
]

/** What a board of rows draws on before it lands. Data decides the real count: 8 is a quiet day. */
const PENDING_ROWS = 8

/** The tab row with the date's pending shape where the picker will be. */
const PENDING_TOOLBAR = <ToolbarSkeleton start={<TabsSkeleton />} end={<PickerSkeleton />} />

/*
 * The loaded page, block for block: the tab row (the session picker at its
 * right) above the table panel on two-line rows. No pager and no footnote: the
 * list is one answer, and the frame draws neither (owner 2026-10-12).
 */
function BreakoutsPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading breakouts…"
      header={HEADER}
      {...signalsRailProps(TITLE)}
    >
      <TableFrameSkeleton
        rows={PENDING_ROWS}
        columns={4}
        toolbar={PENDING_TOOLBAR}
        pager={false}
        tableClassName={SIGNAL_ROWS_52}
      />
    </ScreenLayout>
  )
}

function BreakoutsError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={HEADER}
      pageTitle={TITLE}
      title="Breakouts did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/breakouts')({
  validateSearch: breakoutsSearchSchema,
  loader: loadBreakouts,
  pendingComponent: BreakoutsPending,
  errorComponent: BreakoutsError,
  component: BreakoutsScreen,
})

/**
 * The mock's figure columns (`COMPARE-sz2` §1): 14/400, LTP 110 in the primary ink, Level crossed
 * 130 and When 120 in the secondary grey, all right-aligned and hugging the right edge, the
 * Company column taking the rest. The "LTP (Rs)" head is a glossary term (S9), its card the
 * table-head band's.
 *
 * "When" is the session the answer is for, the same date on every row: the
 * API has no per-row crossing time, and the frame draws the date (owner
 * 2026-10-12, replacing the audit's "From the level" distance). Money columns
 * carry no "Rs" (the headers say what they are). The API sends rows A→Z and so
 * does the table (D6): no column sorts.
 */
const columnsFor = (date: string): readonly DataTableColumn<LevelRow>[] => [
  signalCompanyColumn<LevelRow>({ fill: true }),
  {
    id: 'ltp',
    accessorFn: (row) => row.ltp ?? '',
    header: () => (
      <TermTip term="price" variant="band">
        LTP (Rs)
      </TermTip>
    ),
    enableSorting: false,
    meta: { numeric: true, width: '122px' },
    cell: ({ row }) =>
      row.original.ltp === null ? <MissingFigure /> : <Money value={row.original.ltp} />,
  },
  {
    id: 'level',
    accessorFn: (row) => row.level ?? '',
    header: 'Level crossed',
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: '142px' },
    cell: ({ row }) =>
      row.original.level === null ? <MissingFigure /> : <Money value={row.original.level} />,
  },
  {
    id: 'when',
    accessorFn: () => date,
    header: 'When',
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: '120px', hideBelow: 'sm' },
    cell: () => formatDate(date),
  },
]

const NOTHING_TITLE: Record<BreakoutDirection, string> = {
  above: 'No names pushed above a level',
  below: 'No names fell below a level',
}

/**
 * Signal Zone · Breakout finder — the names whose close on one session crossed
 * a level, above or below, A→Z (the API's order).
 *
 * MAIN read keyed on `?direction=` and `?date=`, so `useQuery` +
 * `keepPreviousData`, not suspense: a change keeps the old table on screen,
 * under RefreshRegion's refresh line, and no entrance replays. A 403 is the
 * locked state (D4).
 *
 * `data: null` (dev's default view today) is mm-be's answer for "no names
 * crossed in that direction on that session", an answer and not an error. It
 * says the same for a session nothing was computed for, so only a day
 * `/sessions/` skips gets the session's own empty state; every other null is
 * the direction's.
 */
function BreakoutsScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const query = useBreakouts(breakoutsQueryOf(search))
  const view = resolveViewState(query, { online })
  const board = query.data
  const isUpdating = query.isPlaceholderData
  const indexSymbols = useIndexSymbols()
  // The direction and session on screen are the answer's own while a change loads, so an
  // empty state never contradicts the table shown beside it.
  const shownDirection = useShownValue(search.direction, isUpdating)
  const shown = board?.direction ?? shownDirection
  const session = useSessionStatus(useShownValue(search.date ?? null, isUpdating))

  const tabs = (
    <TabBar
      options={DIRECTIONS}
      value={search.direction}
      ariaLabel="Direction of the breakout"
      idBase={TABS_ID}
      onChange={(direction) => {
        void navigate({
          search: (prev) => ({ ...prev, direction: direction === 'below' ? 'below' : 'above' }),
        })
      }}
    />
  )
  // The session picker sits at the tabs' right (the strip names the page, so the title row is
  // bare).
  const picker = (
    <>
      <UpdatingNote when={isUpdating} />
      <SessionDatePicker
        value={search.date ?? board?.date ?? null}
        onChange={(date) => {
          void navigate({ search: (prev) => ({ ...prev, date }) })
        }}
      />
    </>
  )

  return (
    <ScreenLayout header={HEADER} {...signalsRailProps(TITLE)}>
      {view === 'failed' || view === 'offline' ? (
        isSignalsLocked(query.error) ? (
          <SignalsLocked error={query.error} />
        ) : (
          <div>
            <SignalsToolbar start={tabs} end={picker} />
            <TabPanel idBase={TABS_ID} value={search.direction}>
              <EmptyState
                kind={view}
                size="page"
                onRetry={() => void query.refetch()}
                retrying={query.isFetching}
                meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
                {...(view === 'failed' ? { title: 'Couldn’t load that session' } : {})}
              />
            </TabPanel>
          </div>
        )
      ) : board === undefined ? (
        // Only before any answer at all: the loader has one for the entry URL.
        <TableFrameSkeleton
          rows={PENDING_ROWS}
          columns={4}
          toolbar={PENDING_TOOLBAR}
          pager={false}
          tableClassName={SIGNAL_ROWS_52}
        />
      ) : board === null ? (
        <div>
          <SignalsToolbar start={tabs} end={picker} />
          <TabPanel idBase={TABS_ID} value={search.direction}>
            <RefreshRegion updating={isUpdating}>
              {session.computed === false ? (
                <SignalsEmpty reason="session" date={session.date} />
              ) : (
                <SignalsEmpty
                  reason="list"
                  title={`${NOTHING_TITLE[shown]} ${onSession(session.date)}`}
                  description="Try the other direction, or pick another session."
                />
              )}
            </RefreshRegion>
          </TabPanel>
        </div>
      ) : (
        <SignalsTableFrame toolbar={tabs} toolbarEnd={picker}>
          <TabPanel idBase={TABS_ID} value={search.direction}>
            <RefreshRegion updating={isUpdating}>
              <DataTable
                columns={columnsFor(board.date)}
                rows={board.rows}
                getRowId={(row) => row.stock.symbol}
                rowLinkOptions={signalRowLink(indexSymbols)}
                rowHeight="lg"
                bodySize="m"
                headWeight="regular"
                washTone="wash"
                nameUnderline={false}
                className={SIGNAL_ROWS_52}
                caption={`Names that ${shown === 'above' ? 'pushed above' : 'fell below'} a level on ${formatDate(board.date)}`}
              />
            </RefreshRegion>
          </TabPanel>
        </SignalsTableFrame>
      )}
    </ScreenLayout>
  )
}
